
import { useState, useRef, useEffect } from 'react';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Loader2,
  RefreshCw
} from 'lucide-react';

import api from '../../utils/api';
import './ChatWidget.css';

const WELCOME_MSG = {
  id: 'welcome',
  role: 'assistant',
  content: "Hi there! 👋 Welcome! Before we get started, may I know your name?",
  ts: Date.now()
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);

  const [messages, setMessages] = useState([WELCOME_MSG]);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Customer information
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');

  // Customer collection steps
  const [customerStep, setCustomerStep] = useState('name');

  // Prevent customer from being saved more than once
  const [customerSaved, setCustomerSaved] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  /*
   * Scroll to latest message
   */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, loading]);

  /*
   * Focus input when chat opens
   */
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  /*
   * Add assistant message
   */
  const addAssistantMessage = (content) => {
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        role: 'assistant',
        content,
        ts: Date.now()
      }
    ]);
  };

  /*
   * Add user message
   */
  const addUserMessage = (content) => {
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        role: 'user',
        content,
        ts: Date.now()
      }
    ]);
  };

  /*
   * Save customer information to backend
   *
   * IMPORTANT:
   * We pass name/mobile directly to this function.
   * We do NOT depend on React state immediately after setState().
   */
  const saveCustomer = async (name, mobile) => {
    try {
      const response = await api.post('/api/customers', {
        name: name.trim(),
        mobile: mobile.trim()
      });

      if (response.data?.success) {
        setCustomerSaved(true);

        console.log('Customer saved successfully:', response.data);

        return true;
      }

      return false;
    } catch (err) {
      console.error('Customer save error:', err);

      const message =
        err.response?.data?.message ||
        'Unable to save your details right now.';

      setError(message);

      return false;
    }
  };

  /*
   * Validate mobile number
   */
  const isValidMobile = (mobile) => {
    const cleaned = mobile.replace(/\D/g, '');

    // Supports 10 digit Indian mobile number
    return /^[6-9]\d{9}$/.test(cleaned);
  };

  /*
   * Handle customer information collection
   */
  const handleCustomerCollection = async (text) => {
    /*
     * STEP 1: CUSTOMER NAME
     */
    if (customerStep === 'name') {
      const name = text.trim();

      if (!name) {
        addAssistantMessage(
          'Please enter your name so I can assist you.'
        );
        return;
      }

      if (name.length < 2) {
        addAssistantMessage(
          'Please enter a valid name.'
        );
        return;
      }

      // Save the actual value immediately.
      // Do not depend on customerName state here.
      setCustomerName(name);

      addUserMessage(name);

      setCustomerStep('mobile');

      addAssistantMessage(
        `Nice to meet you, ${name}! 😊 May I have your mobile number for future reference?`
      );

      return;
    }

    /*
     * STEP 2: CUSTOMER MOBILE
     */
    if (customerStep === 'mobile') {
      const mobile = text.trim();

      if (!isValidMobile(mobile)) {
        addUserMessage(mobile);

        addAssistantMessage(
          'Please enter a valid 10-digit mobile number.'
        );

        return;
      }

      /*
       * IMPORTANT:
       *
       * Use the local customerName variable from state.
       * At this point the name was already stored during step 1.
       */
      const name = customerName.trim();

      if (!name) {
        addAssistantMessage(
          'I could not retrieve your name. Please enter your name again.'
        );

        setCustomerStep('name');

        return;
      }

      // Clean mobile number
      const cleanedMobile = mobile.replace(/\D/g, '');

      setCustomerMobile(cleanedMobile);

      addUserMessage(mobile);

      setLoading(true);
      setError(null);

      /*
       * SAVE CUSTOMER
       */
      const saved = await saveCustomer(
        name,
        cleanedMobile
      );

      setLoading(false);

      if (!saved) {
        addAssistantMessage(
          'I’m sorry, I couldn’t save your details right now. Please try again.'
        );

        return;
      }

      /*
       * Customer successfully saved.
       */
      setCustomerStep('chat');

      addAssistantMessage(
        'Thank you! 😊 Your details have been saved. How can I help you today?'
      );

      return;
    }
  };

  /*
   * Send normal AI chat message
   */
  const sendChatMessage = async (text) => {
    try {
      setLoading(true);
      setError(null);

      /*
       * Build conversation history.
       *
       * Exclude welcome message.
       * Also exclude the customer collection messages from
       * the history sent to the AI if desired.
       */
      const history = messages
        .filter(m => m.id !== 'welcome')
        .map(m => ({
          role: m.role,
          content: m.content
        }))
        .slice(-10);

      const res = await api.post('/api/chat', {
        message: text,
        history
      });

      const reply =
        res.data?.reply ||
        'Sorry, I could not generate a response.';

      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          role: 'assistant',
          content: reply,
          ts: Date.now()
        }
      ]);

    } catch (err) {
      console.error('Chat error:', err);

      const errText =
        err.response?.data?.message ||
        'Something went wrong. Please try again.';

      setError(errText);

    } finally {
      setLoading(false);
    }
  };

  /*
   * Main send function
   */
  const sendMessage = async () => {
    const text = input.trim();

    if (!text || loading) {
      return;
    }

    setInput('');

    /*
     * Customer information collection
     */
    if (!customerSaved && customerStep !== 'chat') {
      await handleCustomerCollection(text);
      return;
    }

    /*
     * Normal chatbot conversation
     */
    addUserMessage(text);

    await sendChatMessage(text);
  };

  /*
   * Enter key
   */
  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /*
   * Reset conversation
   */
  const resetChat = () => {
    setMessages([
      {
        ...WELCOME_MSG,
        id: 'welcome',
        ts: Date.now()
      }
    ]);

    setCustomerName('');
    setCustomerMobile('');

    setCustomerStep('name');
    setCustomerSaved(false);

    setInput('');
    setError(null);
    setLoading(false);
  };

  return (
    <>
      {/* Chat button */}
      <button
        className={`cw-fab ${open ? 'cw-fab--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? (
          <X size={22} />
        ) : (
          <MessageCircle size={22} />
        )}

        {!open && (
          <span className="cw-fab-badge">
            Chat
          </span>
        )}
      </button>

      {/* Chat panel */}
      <div
        className={`cw-panel ${
          open ? 'cw-panel--open' : ''
        }`}
        role="dialog"
        aria-label="Chat support"
      >

        {/* Header */}
        <div className="cw-header">

          <div className="cw-header-avatar">
            <Bot size={18} />
          </div>

          <div className="cw-header-info">
            <span className="cw-header-name">
              Support Assistant
            </span>

            <span className="cw-header-status">
              <span className="cw-dot" />
              Online
            </span>
          </div>

          <button
            className="cw-header-reset"
            onClick={resetChat}
            title="Clear chat"
            aria-label="Clear chat"
          >
            <RefreshCw size={14} />
          </button>

          <button
            className="cw-header-close"
            onClick={() => setOpen(false)}
            aria-label="Close chat"
          >
            <X size={16} />
          </button>

        </div>

        {/* Messages */}
        <div className="cw-messages">

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`cw-msg cw-msg--${msg.role}`}
            >

              {msg.role === 'assistant' && (
                <div className="cw-msg-avatar cw-msg-avatar--bot">
                  <Bot size={13} />
                </div>
              )}

              <div className="cw-msg-bubble">
                {msg.content}
              </div>

              {msg.role === 'user' && (
                <div className="cw-msg-avatar cw-msg-avatar--user">
                  <User size={13} />
                </div>
              )}

            </div>
          ))}

          {/* Loading animation */}
          {loading && (
            <div className="cw-msg cw-msg--assistant">

              <div className="cw-msg-avatar cw-msg-avatar--bot">
                <Bot size={13} />
              </div>

              <div className="cw-msg-bubble cw-msg-bubble--typing">
                <span />
                <span />
                <span />
              </div>

            </div>
          )}

          {/* Error */}
          {error && (
            <div className="cw-error">
              <span>
                ⚠️ {error}
              </span>
            </div>
          )}

          <div ref={bottomRef} />

        </div>

        {/* Input */}
        <div className="cw-input-row">

          <textarea
            ref={inputRef}
            className="cw-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={
              customerStep === 'name'
                ? 'Enter your name...'
                : customerStep === 'mobile'
                ? 'Enter your mobile number...'
                : 'Type a message...'
            }
            rows={1}
            maxLength={500}
            aria-label="Chat input"
          />

          <button
            className="cw-send"
            onClick={sendMessage}
            disabled={
              !input.trim() ||
              loading
            }
            aria-label="Send message"
          >

            {loading ? (
              <Loader2
                size={16}
                className="cw-spin"
              />
            ) : (
              <Send size={16} />
            )}

          </button>

        </div>

        {/* Footer */}
        <div className="cw-footer">
          Powered by AI · {input.length}/500
        </div>

      </div>
    </>
  );
}

